import { Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/atoms/Button';
import { Input } from '@/components/atoms/Input';
import type { ActivityFormData } from '../ActivityEditorPage.types';
import styles from './TypeSpecificFields.module.scss';

interface SentenceCompleteTapFieldsProps {
  textWithBlanks: string;
  singleCorrectAnswer: string;
  words: string[];
  onChange: (updates: Partial<ActivityFormData>) => void;
  errors?: {
    textWithBlanks?: string;
    singleCorrectAnswer?: string;
    words?: string;
  };
}

export function SentenceCompleteTapFields({
  textWithBlanks,
  singleCorrectAnswer,
  words,
  onChange,
  errors,
}: SentenceCompleteTapFieldsProps) {
  const handleWordChange = (index: number, value: string) => {
    const newWords = [...words];
    newWords[index] = value;
    onChange({ words: newWords });
  };

  const handleAddWord = () => {
    onChange({ words: [...words, ''] });
  };

  const handleRemoveWord = (index: number) => {
    if (words.length <= 2) return;
    onChange({ words: words.filter((_, i) => i !== index) });
  };

  const blankCount = (textWithBlanks.match(/_____/g) || []).length;

  return (
    <div className={styles.typeFields}>
      <h3 className={styles.typeFields__title}>Completar la frase (un hueco, palabras con tap)</h3>
      <p className={styles.typeFields__description}>
        Una frase corta con exactamente <strong>un solo hueco</strong> (<code>_____</code>).
        El alumno elige entre 3–4 chips. Usa <strong>_____</strong> (5 guiones bajos) para el hueco.
      </p>

      <div>
        <label htmlFor="textWithBlanksSCT" className={styles.typeFields__title}>
          Frase con hueco
        </label>
        <Input
          id="textWithBlanksSCT"
          value={textWithBlanks}
          onChange={(e) => onChange({ textWithBlanks: e.target.value })}
          placeholder="Ej: She _____ to work every day."
        />
        {blankCount > 0 && (
          <p className={styles.typeFields__hint}>
            {blankCount} {blankCount !== 1 ? 'huecos encontrados' : 'hueco encontrado'}
            {blankCount > 1 && ' — solo se permite uno'}
          </p>
        )}
        {errors?.textWithBlanks && (
          <span className={styles.typeFields__error}>{errors.textWithBlanks}</span>
        )}
      </div>

      <div>
        <label htmlFor="singleCorrectAnswerSCT" className={styles.typeFields__title}>
          Respuesta correcta
        </label>
        <Input
          id="singleCorrectAnswerSCT"
          value={singleCorrectAnswer}
          onChange={(e) => onChange({ singleCorrectAnswer: e.target.value })}
          placeholder="Ej: walks"
        />
        <p className={styles.typeFields__hint}>
          Debe estar incluida también en el pool de palabras.
        </p>
        {errors?.singleCorrectAnswer && (
          <span className={styles.typeFields__error}>{errors.singleCorrectAnswer}</span>
        )}
      </div>

      <div>
        <label className={styles.typeFields__title}>
          Palabras a elegir ({words.length}) — correcta + distractores
        </label>
        <p className={styles.typeFields__description}>
          Incluye la respuesta correcta más 2–3 distractores. Se mostrarán como chips.
        </p>
        <div className={styles.typeFields__list}>
          {words.map((word, index) => (
            <div key={index} className={styles.typeFields__optionItem}>
              <span style={{ minWidth: '24px', textAlign: 'center', color: '#6B7280' }}>
                {index + 1}.
              </span>
              <div className={styles.typeFields__optionInput}>
                <Input
                  value={word}
                  onChange={(e) => handleWordChange(index, e.target.value)}
                  placeholder={`Palabra ${index + 1}`}
                />
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                leftIcon={<Trash2 />}
                onClick={() => handleRemoveWord(index)}
                disabled={words.length <= 2}
                aria-label={`Eliminar palabra ${index + 1}`}
              >
                <span className="sr-only">Eliminar</span>
              </Button>
            </div>
          ))}
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          leftIcon={<Plus />}
          onClick={handleAddWord}
          className={styles.typeFields__addButton}
        >
          Añadir palabra
        </Button>
        {errors?.words && <span className={styles.typeFields__error}>{errors.words}</span>}
      </div>
    </div>
  );
}
